import { useEffect, useState } from 'react'

import { API } from '@/api'
import { Checkbox, Loading, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { ClusterInstance } from '@/models/mesh'
import { Network } from '@/models/network'
import { cn } from '@/utils/style'

interface ClusterInstanceSourceTableProps {
  network: Network
  regionDisplayName: string
  targetKeys: React.Key[]
  selectedKeys: React.Key[]
  onSourceUpdated: (rows: ClusterInstance[]) => void
  onSelectedRowsChange?: (rows: React.Key[]) => void
}

export default function ClusterInstanceSourceTable({
  network,
  regionDisplayName,
  targetKeys,
  selectedKeys: propSelectedKeys,
  onSourceUpdated,
  onSelectedRowsChange,
}: ClusterInstanceSourceTableProps) {
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  // all data
  const [sourceInstanceList, setSourceInstanceList] = useState<ClusterInstance[]>([])
  // current page data
  const [instaceList, setInstanceList] = useState<ClusterInstance[]>([])
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])

  const pageSize = 4

  const onSourceInstaceListUpdated = (newInstances: ClusterInstance[]) => {
    console.log(sourceInstanceList)
    setSourceInstanceList((prevSourceList) => {
      const mergedList = [...prevSourceList, ...newInstances]
      const uniqueList = Array.from(new Set(mergedList.map((instance) => instance.resID))).map(
        (resID) => mergedList.find((instance) => instance.resID === resID)!,
      )
      onSourceUpdated?.(uniqueList)

      return uniqueList
    })
  }

  const fetchData = async (p = page, force: boolean) => {
    if (force) {
      setLoading(true)
    }
    try {
      const res = await API.getMeshEndpointCandidates({
        res_id: network.resID,
        page: p,
        per_page: pageSize,
      })
      if (res.code == 0) {
        setPage(res.pagination.page)
        setTotal(res.pagination.total)
        const nodes = res.instances?.map((data) => new ClusterInstance(data)) ?? []
        setInstanceList(nodes)
        onSourceInstaceListUpdated(nodes)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, true)
  }, [network])

  useEffect(() => {
    setInstanceList((prev) =>
      prev.map((instance) =>
        targetKeys?.includes(instance.resID) ? { ...instance, disabled: true } : instance,
      ),
    )
  }, [targetKeys, page])

  useEffect(() => {
    setSelectedKeys(propSelectedKeys)
  }, [propSelectedKeys])

  const handleRowSelection = (item: ClusterInstance, checked: boolean) => {
    let updatedRows = []
    if (checked) {
      updatedRows = [...selectedKeys, item.resID]
    } else {
      updatedRows = selectedKeys.filter((key) => key !== item.resID)
    }
    setSelectedKeys(updatedRows)
    onSelectedRowsChange?.(updatedRows)
  }

  return (
    <div className="rounded-2xl border border-neutral-200 p-4">
      <div className="min-h-[32px] flex items-center">
        <div className="t-sb text-primary">
          <Trans i18nKey="mesh.clusterAddInsModal.myInstance">My Deployments</Trans>
        </div>
      </div>

      <div className="h-[396px] mt-2">
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <table className="table table-fixed w-full">
            <thead>
              <tr className="rounded-lg">
                <th className="w-auto rounded-l-lg"></th>
                <th className="w-[30%]">
                  <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderID">ID</Trans>
                </th>
                <th className="w-[40%]">
                  <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderName">Name</Trans>
                </th>
                <th className="w-[20%] rounded-r-lg">
                  <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderRegion">Region</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {instaceList.length > 0 &&
                instaceList.map((data, idx) => (
                  <tr
                    key={`${data.resID}${idx}`}
                    className={cn('h-[72px]', {
                      'pointer-events-none opacity-20': targetKeys?.includes(data.resID),
                    })}
                  >
                    <td>
                      <Checkbox
                        checked={
                          targetKeys?.includes(data.resID) || selectedKeys?.includes(data.resID)
                        }
                        onChange={(checked) => handleRowSelection(data, checked)}
                      />
                    </td>
                    <td className="w-[30%] t-sm text-primary">{data?.resID ?? '-'}</td>
                    <td className="w-[40%] t-sm text-primary">
                      {data?.gpuName ?? data.cpuName ?? '-'}
                    </td>
                    <td className="w-[20%] t-sm text-primary">{regionDisplayName}</td>
                  </tr>
                ))}
              {instaceList.length === 0 && (
                <tr className="">
                  <td colSpan={3} className="border-none">
                    <EmptyView
                      content={
                        <span className="t-sm text-secondary">
                          <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
                        </span>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {total > 0 && !loading && (
          <Pagination
            size={pageSize}
            total={total}
            current={page}
            className="justify-end mt-4"
            onChange={(page) => fetchData(page, true)}
          />
        )}
      </div>
    </div>
  )
}
